/**
 * Указатель перенесённых из WordPress статей — /articles/.
 *
 * Нужен не «для полноты», а ради достижимости: в WordPress эти 734 страницы
 * держал архив /blog/, в статике их не держало бы ничто, и сторож связности
 * (`scripts/check-connectivity.js`) справедливо считал их островами. Ссылка на
 * указатель стоит в подвале, то есть на каждой странице сайта.
 *
 * Список приходит из /_articles/_index.json (адрес + заголовок, без тел).
 * Готовую страницу порождает scripts/generate-legacy-pages.mjs, он же кладёт
 * список в window.__ARTICLE_INDEX__ — поэтому первая отрисовка совпадает с тем,
 * что уже отдано браузеру.
 */
import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';

interface Item {
  url: string;
  title: string;
  count?: number;
}

declare global {
  interface Window {
    __ARTICLE_INDEX__?: Item[];
  }
}

function useIndex(): Item[] | null {
  const inline = typeof window !== 'undefined' ? window.__ARTICLE_INDEX__ ?? null : null;
  const [items, setItems] = useState<Item[] | null>(inline);
  useEffect(() => {
    if (inline) return;
    let alive = true;
    fetch('/_articles/_hubs.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: Item[]) => { if (alive) setItems(d); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [inline]);
  return items;
}

function groupByLetter(items: Item[]): [string, Item[]][] {
  const groups = new Map<string, Item[]>();
  for (const it of items) {
    const key = /^[a-z]/i.test(it.title) ? it.title[0].toUpperCase() : '#';
    const list = groups.get(key);
    if (list) list.push(it); else groups.set(key, [it]);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

export default function ArticleLibraryPage() {
  const items = useIndex();
  useEffect(() => {
    if (typeof document !== 'undefined') document.title = 'Article Library | Morgan Legal Group New York';
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main id="article-library">
        <section className="relative bg-navy pt-28 lg:pt-32 pb-14 lg:pb-16">
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]">
              Article Library
            </h1>
            <p className="font-body text-white/80 mt-5">
              {(items ?? []).reduce((n, h) => n + (h.count ?? 0), 0)} articles across {items?.length ?? 0} topics
              — New York estate planning, probate, elder law and related matters.
            </p>
          </div>
        </section>
        <section className="py-14 lg:py-20 bg-cream">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 font-body text-navy/85">
            {groupByLetter(items ?? []).map(([letter, list]) => (
              <div key={letter}>
                <h2 className="font-display text-navy text-2xl font-bold mt-10 mb-4">{letter}</h2>
                <ul className="space-y-2">
                  {list.map((it) => (
                    <li key={it.url}>
                      <a
                        className="text-navy underline decoration-gold/60 underline-offset-2 hover:text-gold"
                        href={it.url}
                      >
                        {it.title}
                      </a>
                      {it.count ? <span className="text-navy/50"> — {it.count}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
